import { useEffect, useRef } from 'react';
import { getLenis } from '@/lib/smoothScroll';

// Escape to close, body scroll lock, initial focus, simple focus trap and focus restore.
// onClose is kept in a ref so parent re-renders (e.g. quantity changes) never re-run the setup.
export default function useOverlay(onClose: () => void) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    getLenis()?.stop();
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>('[data-autofocus]')?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const els = Array.from(panel.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input, select'));
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      html.style.overflow = prevOverflow;
      getLenis()?.start();
      prevFocus?.focus?.({ preventScroll: true });
    };
  }, []);

  return panelRef;
}